import { ShareMomentKind } from "@/main/util/PlatformIdentifierUtil";

/**
 * The arming parameter for a share moment: `?sv_m=post_publish` / `?sv_m=first_response`.
 *
 * <p>One parameter, two values, one reader. Namespaced like `sv_s` (the share token) and `sv_attrib`, and
 * verified free of collisions across the frontend, backend Java, JTE and the properties bundles before it
 * was minted.
 *
 * <p>It is opaque and carries no personal data, which matters because an author could paste the URL from
 * the address bar in the one paint before the strip effect runs. Even then the reader is author-gated, so
 * a recipient who follows such a link sees nothing.
 */
export const SHARE_MOMENT_PARAM = "sv_m";

const KNOWN_KINDS: ReadonlySet<string> = new Set<ShareMomentKind>([
    "post_publish",
    "first_response",
    "first_post",
    // The pride moments (round-26 A4). Nothing in this package WRITES these three — the backend appends
    // them to the notification's own URL — so for them this set is purely the reader's allowlist, and
    // the strings must match `AnswerAcceptedNotification` / `TopReplyNotification` /
    // `DiscussionGainingTractionNotification` byte for byte.
    "answer_accepted",
    "top_reply",
    "gaining_traction",
]);

/**
 * Appends `?sv_m=<kind>` to a redirect target when the post is eligible.
 *
 * <p>Returns the URL untouched when it is not — the caller decides eligibility, because the caller is the
 * publish site where `visibilityTier` and `privateAccess` are already in hand and no extra request is
 * needed to learn them.
 *
 * <p><b>Eligibility and KIND are two separate questions, and keeping them separate is the point of
 * tier-2 finding 22.</b> `eligible` still decides whether a moment fires at all, so the first-post
 * variant inherits every existing suppression — most importantly it stays SILENT when
 * `visibilityTier !== "NORMAL"`. Congratulating an author on their first post at the moment it was
 * demoted is the worst possible time to speak, and that is a property of the guard rather than
 * something this function has to remember.
 */
export function withShareMomentParam(
    url: string,
    options: { eligible: boolean; kind?: ShareMomentKind },
): string {
    if (!options.eligible || !url) {
        return url;
    }
    const kind: ShareMomentKind = options.kind ?? "post_publish";
    // Insert before any fragment, for the same reason the share token does: a parameter placed after `#`
    // lives inside the fragment, where the reader's `location.search` never sees it.
    const hashIndex = url.indexOf("#");
    const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const fragment = hashIndex >= 0 ? url.slice(hashIndex) : "";
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${SHARE_MOMENT_PARAM}=${kind}${fragment}`;
}

/** Reads a known moment kind out of a query string, or null. An unknown value is ignored, not trusted. */
export function readShareMomentKind(search: string): ShareMomentKind | null {
    if (!search) {
        return null;
    }
    try {
        const value = new URLSearchParams(search).get(SHARE_MOMENT_PARAM);
        return value && KNOWN_KINDS.has(value) ? (value as ShareMomentKind) : null;
    } catch {
        return null;
    }
}

/** The same URL with the arming parameter removed, preserving everything else including the fragment. */
export function withoutShareMomentParam(href: string): string {
    try {
        const url = new URL(href, typeof window !== "undefined" ? window.location.origin : "https://seavien.com");
        url.searchParams.delete(SHARE_MOMENT_PARAM);
        return url.pathname + (url.search ? url.search : "") + url.hash;
    } catch {
        return href;
    }
}
